import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useAuth } from "./AuthContext";
import { useRealtime } from "./RealtimeContext";

const ROUnreadContext = createContext(null);
const MAX_JOURNAL_ENTRIES = 200;
const MAX_RETRACTED_MESSAGE_TOMBSTONES_PER_RO = 100;

const EMPTY_UNREAD = Object.freeze({
  roId: "",
  unread: false,
  latestUnreadMessageId: null,
  latestUnreadMessageAuthorUserId: "",
  latestUnreadMessageCreatedAt: "",
  lastReadMessageId: null,
  updatedAt: "",
  unreadIdentityUnknown: false,
});

const INITIAL_STATE = Object.freeze({
  byRo: {},
  isHydrated: false,
  isLoading: false,
  error: "",
  storeId: "",
  baselineInitialized: false,
  baselineMessageId: null,
  snapshotMaxMessageId: null,
  retractedMessageIdsByRo: {},
});

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function normalizeDecimalId(value) {
  const raw = safeString(value).trim();
  if (!/^\d+$/.test(raw)) return "";
  const normalized = raw.replace(/^0+/, "") || "0";
  return normalized === "0" ? "" : normalized;
}

function isDecimalMessageId(value) {
  return !!normalizeDecimalId(value);
}

function compareDecimalStrings(a, b) {
  const left = normalizeDecimalId(a);
  const right = normalizeDecimalId(b);

  if (!left && !right) return 0;
  if (!left) return -1;
  if (!right) return 1;
  if (left.length !== right.length) return left.length - right.length;
  return left.localeCompare(right);
}

function isGreaterDecimal(a, b) {
  return compareDecimalStrings(a, b) > 0;
}

function normalizeUnreadItem(input, fallbackStoreId) {
  const item = input && typeof input === "object" ? input : {};
  const roId = safeString(item.roId).trim();
  const latestUnreadMessageId = normalizeDecimalId(item.latestUnreadMessageId);
  if (!roId || !latestUnreadMessageId) return null;

  return {
    roId,
    unread: true,
    latestUnreadMessageId,
    latestUnreadMessageAuthorUserId: safeString(
      item.latestUnreadMessageAuthorUserId
    ).trim(),
    latestUnreadMessageCreatedAt: safeString(
      item.latestUnreadMessageCreatedAt
    ).trim(),
    lastReadMessageId: normalizeDecimalId(item.lastReadMessageId) || null,
    updatedAt: safeString(item.updatedAt).trim(),
    storeId: safeString(item.storeId || fallbackStoreId).trim(),
    unreadIdentityUnknown: false,
  };
}

function normalizeServerMessage(input) {
  const message = input && typeof input === "object" ? input : {};

  return {
    id: normalizeDecimalId(message.id),
    storeId: safeString(message.storeId).trim(),
    roId: safeString(message.roId).trim(),
    authorUserId: safeString(message.authorUserId).trim(),
    createdAt: safeString(message.createdAt).trim(),
  };
}

function normalizeReadEvent(input) {
  const event = input && typeof input === "object" ? input : {};

  return {
    storeId: safeString(event.storeId).trim(),
    roId: safeString(event.roId).trim(),
    userId: safeString(event.userId).trim(),
    lastReadMessageId: normalizeDecimalId(event.lastReadMessageId),
    updatedAt: safeString(event.updatedAt).trim(),
  };
}

function getEntry(state, roId) {
  return state.byRo[roId] || { ...EMPTY_UNREAD, roId };
}

function effectiveReadBoundary(state, roId, entryValue) {
  const entry = entryValue || getEntry(state, roId);
  const baseline = normalizeDecimalId(state?.baselineMessageId);
  const cursor = normalizeDecimalId(entry?.lastReadMessageId);
  if (isGreaterDecimal(cursor, baseline)) return cursor;
  return baseline || cursor || null;
}

function clearUnreadIdentity(entry) {
  return {
    ...entry,
    unread: false,
    latestUnreadMessageId: null,
    latestUnreadMessageAuthorUserId: "",
    latestUnreadMessageCreatedAt: "",
    unreadIdentityUnknown: false,
  };
}

function isKnownRetractedMessage(state, roId, messageId) {
  const ids = state?.retractedMessageIdsByRo?.[roId];
  return Array.isArray(ids) && ids.includes(messageId);
}

function withRetractedMessageTombstone(state, roId, messageId) {
  if (!roId || !messageId) return state;

  const current = Array.isArray(state.retractedMessageIdsByRo?.[roId])
    ? state.retractedMessageIdsByRo[roId]
    : [];
  const nextIds = current.includes(messageId)
    ? current
    : [...current, messageId].slice(-MAX_RETRACTED_MESSAGE_TOMBSTONES_PER_RO);

  if (nextIds === current) return state;

  return {
    ...state,
    retractedMessageIdsByRo: {
      ...(state.retractedMessageIdsByRo || {}),
      [roId]: nextIds,
    },
  };
}

function reconcileEntryWithBoundary(state, roId, entryValue) {
  const entry = entryValue || getEntry(state, roId);
  const boundary = effectiveReadBoundary(state, roId, entry);

  if (
    entry.latestUnreadMessageId &&
    boundary &&
    compareDecimalStrings(entry.latestUnreadMessageId, boundary) <= 0
  ) {
    return clearUnreadIdentity(entry);
  }

  return entry;
}

function applyMessageCreated(state, transition) {
  const roId = safeString(transition?.roId).trim();
  const messageId = normalizeDecimalId(transition?.messageId);
  if (!roId || !messageId) return state;
  if (isKnownRetractedMessage(state, roId, messageId)) return state;

  const current = getEntry(state, roId);
  const boundary = effectiveReadBoundary(state, roId, current);
  if (
    boundary &&
    compareDecimalStrings(messageId, boundary) <= 0
  ) {
    return state;
  }

  const shouldReplaceLatest =
    !current.latestUnreadMessageId ||
    isGreaterDecimal(messageId, current.latestUnreadMessageId);

  return {
    ...state,
    byRo: {
      ...state.byRo,
      [roId]: {
        ...current,
        unread: true,
        latestUnreadMessageId: shouldReplaceLatest
          ? messageId
          : current.latestUnreadMessageId,
        latestUnreadMessageAuthorUserId: shouldReplaceLatest
          ? safeString(transition.authorUserId).trim()
          : current.latestUnreadMessageAuthorUserId,
        latestUnreadMessageCreatedAt: shouldReplaceLatest
          ? safeString(transition.createdAt).trim()
          : current.latestUnreadMessageCreatedAt,
        unreadIdentityUnknown: false,
      },
    },
  };
}

function applyMessageRetracted(state, transition) {
  const roId = safeString(transition?.roId).trim();
  const messageId = normalizeDecimalId(transition?.messageId);
  if (!roId || !messageId) return state;

  const tombstoned = withRetractedMessageTombstone(state, roId, messageId);
  const current = getEntry(tombstoned, roId);

  if (current.latestUnreadMessageId !== messageId) return tombstoned;

  return {
    ...tombstoned,
    byRo: {
      ...tombstoned.byRo,
      [roId]: {
        ...current,
        unread: true,
        latestUnreadMessageId: null,
        latestUnreadMessageAuthorUserId: "",
        latestUnreadMessageCreatedAt: "",
        unreadIdentityUnknown: true,
      },
    },
  };
}

function applyRead(state, transition) {
  const roId = safeString(transition?.roId).trim();
  const lastReadMessageId = normalizeDecimalId(transition?.lastReadMessageId);
  if (!roId || !lastReadMessageId) return state;

  const current = getEntry(state, roId);
  const nextRead =
    !current.lastReadMessageId ||
    isGreaterDecimal(lastReadMessageId, current.lastReadMessageId)
      ? lastReadMessageId
      : current.lastReadMessageId;

  const nextEntry = {
    ...current,
    lastReadMessageId: nextRead,
    updatedAt: safeString(transition.updatedAt).trim() || current.updatedAt,
  };
  const boundary = effectiveReadBoundary(state, roId, nextEntry);
  const hasLaterUnread =
    current.latestUnreadMessageId &&
    isGreaterDecimal(current.latestUnreadMessageId, boundary);
  const hasUnknownUnread = current.unreadIdentityUnknown === true;

  return {
    ...state,
    byRo: {
      ...state.byRo,
      [roId]: {
        ...nextEntry,
        unread: !!hasLaterUnread || hasUnknownUnread,
        latestUnreadMessageId: hasLaterUnread
          ? current.latestUnreadMessageId
          : null,
        latestUnreadMessageAuthorUserId: hasLaterUnread
          ? current.latestUnreadMessageAuthorUserId
          : "",
        latestUnreadMessageCreatedAt: hasLaterUnread
          ? current.latestUnreadMessageCreatedAt
          : "",
        unreadIdentityUnknown: hasUnknownUnread,
      },
    },
  };
}

function applyMarkReadTransition(state, action) {
  const roId = safeString(action.roId).trim();
  const lastReadMessageId = normalizeDecimalId(action.lastReadMessageId);
  if (!roId || !lastReadMessageId) return state;

  const current = getEntry(state, roId);
  if (
    current.lastReadMessageId &&
    compareDecimalStrings(lastReadMessageId, current.lastReadMessageId) < 0
  ) {
    return state;
  }

  let next = applyRead(state, {
    roId,
    lastReadMessageId,
    updatedAt: action.updatedAt,
  });

  if (action.unread === true) {
    const nextEntry = getEntry(next, roId);
    next = {
      ...next,
      byRo: {
        ...next.byRo,
        [roId]: {
          ...nextEntry,
          unread: true,
          unreadIdentityUnknown: !nextEntry.latestUnreadMessageId,
        },
      },
    };
  } else if (action.unread === false) {
    const nextEntry = getEntry(next, roId);
    const boundary = effectiveReadBoundary(next, roId, nextEntry);
    const hasLaterUnread =
      nextEntry.latestUnreadMessageId &&
      isGreaterDecimal(nextEntry.latestUnreadMessageId, boundary);
    next = {
      ...next,
      byRo: {
        ...next.byRo,
        [roId]: hasLaterUnread
          ? {
              ...nextEntry,
              unread: true,
              unreadIdentityUnknown: false,
            }
          : clearUnreadIdentity(nextEntry),
      },
    };
  }

  return next;
}

function applyTransition(state, transition) {
  if (transition?.type === "messageCreated") {
    return applyMessageCreated(state, transition);
  }
  if (transition?.type === "messageRetracted") {
    return applyMessageRetracted(state, transition);
  }
  if (transition?.type === "messagesRead") {
    return applyRead(state, transition);
  }
  if (transition?.type === "markReadResult") {
    return applyMarkReadTransition(state, transition);
  }
  return state;
}

function replayTransitions(state, transitions) {
  const transitionList = Array.isArray(transitions) ? transitions : [];
  const retractedKeys = new Set(
    transitionList
      .filter((transition) => transition?.type === "messageRetracted")
      .map((transition) => {
        const roId = safeString(transition?.roId).trim();
        const messageId = normalizeDecimalId(transition?.messageId);
        return roId && messageId ? `${roId}\n${messageId}` : "";
      })
      .filter(Boolean)
  );
  const filtered = transitionList.filter((transition) => {
    if (transition?.type !== "messageCreated") return true;

    const roId = safeString(transition?.roId).trim();
    const messageId = normalizeDecimalId(transition?.messageId);
    return !retractedKeys.has(`${roId}\n${messageId}`);
  });

  return filtered.reduce(
    (next, transition) => applyTransition(next, transition),
    state
  );
}

function stateFromSnapshot(data, storeId) {
  const byRo = {};
  const items = Array.isArray(data?.items) ? data.items : [];

  items.forEach((item) => {
    const normalized = normalizeUnreadItem(item, storeId);
    if (normalized) {
      byRo[normalized.roId] = normalized;
    }
  });

  return {
    byRo,
    isHydrated: true,
    isLoading: false,
    error: "",
    storeId: safeString(data?.storeId || storeId).trim(),
    baselineInitialized: !!data?.baselineInitialized,
    baselineMessageId: normalizeDecimalId(data?.baselineMessageId) || null,
    snapshotMaxMessageId: normalizeDecimalId(data?.snapshotMaxMessageId) || null,
    retractedMessageIdsByRo: {},
  };
}

function preserveKnownReadPositions(snapshotState, previousState) {
  const nextByRo = { ...snapshotState.byRo };

  Object.entries(previousState?.byRo || {}).forEach(([roId, previous]) => {
    if (!previous?.lastReadMessageId) return;

    const current = nextByRo[roId];
    if (!current) {
      nextByRo[roId] = {
        ...EMPTY_UNREAD,
        roId,
        lastReadMessageId: previous.lastReadMessageId,
        updatedAt: previous.updatedAt || "",
      };
      return;
    }

    if (
      !current.lastReadMessageId ||
      isGreaterDecimal(previous.lastReadMessageId, current.lastReadMessageId)
    ) {
      nextByRo[roId] = {
        ...current,
        lastReadMessageId: previous.lastReadMessageId,
        updatedAt: previous.updatedAt || current.updatedAt,
      };
    }
  });

  const nextState = {
    ...snapshotState,
    byRo: nextByRo,
    retractedMessageIdsByRo: {
      ...(previousState?.retractedMessageIdsByRo || {}),
    },
  };

  Object.entries(nextState.byRo).forEach(([roId, entry]) => {
    nextState.byRo[roId] = reconcileEntryWithBoundary(nextState, roId, entry);
  });

  return nextState;
}

function reducer(state, action) {
  switch (action.type) {
    case "RESET":
      return {
        ...INITIAL_STATE,
        storeId: safeString(action.storeId).trim(),
      };

    case "HYDRATE_START":
      return {
        ...state,
        isLoading: true,
        error: "",
        storeId: safeString(action.storeId).trim(),
      };

    case "HYDRATE_SUCCESS":
      return replayTransitions(
        preserveKnownReadPositions(
          stateFromSnapshot(action.data, action.storeId),
          state
        ),
        action.replayTransitions
      );

    case "HYDRATE_ERROR":
      return {
        ...state,
        isLoading: false,
        error: action.error || "Unable to load unread messages.",
      };

    case "APPLY_TRANSITION":
      return applyTransition(state, action.transition);

    case "APPLY_TRANSITION_WITH_REPLAY":
      return replayTransitions(
        applyTransition(state, action.transition),
        action.replayTransitions
      );

    default:
      return state;
  }
}

async function readResponseBody(res) {
  const contentType = safeString(res.headers?.get?.("content-type"));
  if (res.status === 204) return {};

  if (contentType.includes("application/json")) {
    return await res.json().catch(() => ({}));
  }

  const text = await res.text().catch(() => "");
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

export function ROUnreadProvider({ children }) {
  const auth = useAuth();
  const realtime = useRealtime();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const generationRef = useRef(0);
  const storeIdRef = useRef("");
  const userIdRef = useRef("");
  const stateRef = useRef(INITIAL_STATE);
  const revisionRef = useRef(0);
  const journalRef = useRef([]);
  const inFlightMarksRef = useRef(new Map());
  const hydrationSequenceRef = useRef(0);
  const appliedHydrationSequenceRef = useRef(0);
  const markRequestSequenceRef = useRef(0);
  const appliedMarkSequenceByRoRef = useRef(new Map());
  const refreshUnreadRef = useRef(null);
  const refreshRecoveryScheduledRef = useRef(false);
  const unresolvedUnreadRefreshByRoRef = useRef(new Set());
  const wasRealtimeLiveRef = useRef(false);

  const token = safeString(auth?.token).trim();
  const storeId = safeString(auth?.effectiveStoreId || auth?.activeStoreId).trim();
  const userId = safeString(auth?.user?.id || auth?.user?.userId).trim();
  const hydrated = auth?.hydrated !== false;
  const isAuthenticated = !!auth?.isAuthenticated;
  const isStoreMode = auth?.mode !== "platform";
  const isImpersonating = !!auth?.isImpersonating || !!auth?.user?.isImpersonating;
  const apiUrl = auth?.apiUrl;
  const signOut = auth?.signOut;
  const subscribeToRoMessageCreated = realtime?.subscribeToRoMessageCreated;
  const subscribeToRoMessageRetracted = realtime?.subscribeToRoMessageRetracted;
  const subscribeToRoMessagesRead = realtime?.subscribeToRoMessagesRead;
  const isRealtimeLive = !!realtime?.isLive;
  const isReady =
    hydrated &&
    isAuthenticated &&
    isStoreMode &&
    !!token &&
    !!storeId &&
    !!userId &&
    typeof apiUrl === "function";

  useLayoutEffect(() => {
    const resetState = {
      ...INITIAL_STATE,
      storeId,
    };

    generationRef.current += 1;
    storeIdRef.current = storeId;
    userIdRef.current = userId;
    stateRef.current = resetState;
    revisionRef.current = 0;
    journalRef.current = [];
    inFlightMarksRef.current.clear();
    hydrationSequenceRef.current = 0;
    appliedHydrationSequenceRef.current = 0;
    markRequestSequenceRef.current = 0;
    appliedMarkSequenceByRoRef.current.clear();
    refreshRecoveryScheduledRef.current = false;
    unresolvedUnreadRefreshByRoRef.current.clear();
    wasRealtimeLiveRef.current = false;
    dispatch({ type: "RESET", storeId });
  }, [isReady, storeId, token, userId, isImpersonating]);

  useLayoutEffect(() => {
    stateRef.current = state;
  }, [state]);

  const isCurrentSession = useCallback((requestStoreId, requestUserId, generation) => {
    return (
      safeString(requestStoreId).trim() === storeIdRef.current &&
      safeString(requestUserId).trim() === userIdRef.current &&
      generation === generationRef.current
    );
  }, []);

  const handleCurrentSessionAuthError = useCallback(
    (error, requestStoreId, requestUserId, generation) => {
      if (
        error?.status === 401 &&
        isCurrentSession(requestStoreId, requestUserId, generation) &&
        typeof signOut === "function"
      ) {
        signOut();
      }
    },
    [isCurrentSession, signOut]
  );

  const requestJson = useCallback(
    async (method, path, body) => {
      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Store-Id": storeId,
      };

      if (body != null) headers["Content-Type"] = "application/json";

      const res = await fetch(apiUrl(path), {
        method,
        headers,
        body: body != null ? JSON.stringify(body) : undefined,
      });

      const data = await readResponseBody(res);
      if (!res.ok) {
        const error = new Error(
          safeString(data?.error || data?.message) ||
            `${method} failed (${res.status})`
        );
        error.status = res.status;
        error.data = data;
        throw error;
      }

      return data;
    },
    [apiUrl, storeId, token]
  );

  const getReplayTransitions = useCallback((generation, revision) => {
    const currentRevision = revisionRef.current;
    if (currentRevision <= revision) {
      return { complete: true, transitions: [] };
    }

    const retainedForGeneration = journalRef.current.filter(
      (transition) => transition.generation === generation
    );
    const firstRetainedRevision = retainedForGeneration[0]?.revision;

    if (
      !firstRetainedRevision ||
      firstRetainedRevision > revision + 1
    ) {
      return { complete: false, transitions: [] };
    }

    return {
      complete: true,
      transitions: retainedForGeneration.filter(
        (transition) => transition.revision > revision
      ),
    };
  }, []);

  const scheduleRefreshUnread = useCallback(() => {
    if (refreshRecoveryScheduledRef.current) return;

    refreshRecoveryScheduledRef.current = true;
    const runLater =
      typeof window !== "undefined" && typeof window.setTimeout === "function"
        ? window.setTimeout
        : setTimeout;
    runLater(() => {
      refreshRecoveryScheduledRef.current = false;
      if (typeof refreshUnreadRef.current === "function") {
        refreshUnreadRef.current().catch((error) => {
          console.error(error);
        });
      }
    }, 0);
  }, []);

  const recordTransition = useCallback(
    (transition, { dispatchNow = true } = {}) => {
      const generation = generationRef.current;
      const revision = revisionRef.current + 1;
      revisionRef.current = revision;

      const entry = {
        ...transition,
        generation,
        revision,
      };

      journalRef.current = [...journalRef.current, entry].slice(
        -MAX_JOURNAL_ENTRIES
      );
      if (dispatchNow) {
        dispatch({ type: "APPLY_TRANSITION", transition: entry });
      }
      return entry;
    },
    []
  );

  const refreshUnread = useCallback(async () => {
    if (!isReady) return null;

    const requestStoreId = storeId;
    const requestUserId = userId;
    const generation = generationRef.current;
    const startRevision = revisionRef.current;
    const hydrationSequence = hydrationSequenceRef.current + 1;
    hydrationSequenceRef.current = hydrationSequence;

    dispatch({ type: "HYDRATE_START", storeId: requestStoreId });

    try {
      const data = await requestJson("GET", "/api/message-unread");

      if (!isCurrentSession(requestStoreId, requestUserId, generation)) {
        return data;
      }

      if (hydrationSequence < appliedHydrationSequenceRef.current) {
        return data;
      }

      const replay = getReplayTransitions(generation, startRevision);
      if (!replay.complete) {
        scheduleRefreshUnread();
        return data;
      }

      appliedHydrationSequenceRef.current = hydrationSequence;
      dispatch({
        type: "HYDRATE_SUCCESS",
        data,
        storeId: requestStoreId,
        replayTransitions: replay.transitions,
      });

      return data;
    } catch (error) {
      handleCurrentSessionAuthError(
        error,
        requestStoreId,
        requestUserId,
        generation
      );

      if (
        isCurrentSession(requestStoreId, requestUserId, generation) &&
        hydrationSequence >= appliedHydrationSequenceRef.current
      ) {
        dispatch({
          type: "HYDRATE_ERROR",
          error: "Unable to load unread messages.",
        });
      }

      throw error;
    }
  }, [
    handleCurrentSessionAuthError,
    getReplayTransitions,
    isCurrentSession,
    isReady,
    requestJson,
    scheduleRefreshUnread,
    storeId,
    userId,
  ]);

  useLayoutEffect(() => {
    refreshUnreadRef.current = refreshUnread;
  }, [refreshUnread]);

  useEffect(() => {
    if (!isReady) return undefined;

    let cancelled = false;
    refreshUnread().catch((error) => {
      if (!cancelled) console.error(error);
    });

    return () => {
      cancelled = true;
    };
  }, [isReady, refreshUnread]);

  useEffect(() => {
    if (!isReady) {
      wasRealtimeLiveRef.current = false;
      return undefined;
    }

    if (!isRealtimeLive) {
      wasRealtimeLiveRef.current = false;
      return undefined;
    }

    if (wasRealtimeLiveRef.current) return undefined;

    wasRealtimeLiveRef.current = true;
    let cancelled = false;

    refreshUnread().catch((error) => {
      if (!cancelled) console.error(error);
    });

    return () => {
      cancelled = true;
    };
  }, [isReady, isRealtimeLive, refreshUnread]);

  const markRead = useCallback(
    async (roId, lastReadMessageId) => {
      const cleanRoId = safeString(roId).trim();
      const cleanMessageId = normalizeDecimalId(lastReadMessageId);

      if (!isReady || isImpersonating || !cleanRoId || !cleanMessageId) {
        return null;
      }

      const current = getEntry(stateRef.current, cleanRoId);
      const currentBoundary = effectiveReadBoundary(
        stateRef.current,
        cleanRoId,
        current
      );
      if (
        currentBoundary &&
        compareDecimalStrings(cleanMessageId, currentBoundary) <= 0
      ) {
        return {
          roId: cleanRoId,
          lastReadMessageId: currentBoundary,
          skipped: true,
        };
      }

      const inFlight = inFlightMarksRef.current.get(cleanRoId);
      if (
        inFlight?.lastReadMessageId &&
        compareDecimalStrings(cleanMessageId, inFlight.lastReadMessageId) <= 0
      ) {
        return {
          roId: cleanRoId,
          lastReadMessageId: inFlight.lastReadMessageId,
          skipped: true,
        };
      }

      const requestStoreId = storeId;
      const requestUserId = userId;
      const generation = generationRef.current;
      const startRevision = revisionRef.current;
      const markRequestSequence = markRequestSequenceRef.current + 1;
      markRequestSequenceRef.current = markRequestSequence;

      inFlightMarksRef.current.set(cleanRoId, {
        lastReadMessageId: cleanMessageId,
        generation,
        markRequestSequence,
      });

      try {
        const data = await requestJson(
          "POST",
          `/api/ros/${encodeURIComponent(cleanRoId)}/messages/read`,
          { lastReadMessageId: cleanMessageId }
        );

        if (!isCurrentSession(requestStoreId, requestUserId, generation)) {
          return data;
        }

        const acceptedId = normalizeDecimalId(data?.lastReadMessageId);
        if (acceptedId) {
          const latestAppliedSequence =
            appliedMarkSequenceByRoRef.current.get(cleanRoId) || 0;
          if (markRequestSequence < latestAppliedSequence) {
            return data;
          }

          const currentAfterResponse = getEntry(stateRef.current, cleanRoId);
          const currentAfterResponseBoundary = effectiveReadBoundary(
            stateRef.current,
            cleanRoId,
            currentAfterResponse
          );
          if (
            currentAfterResponseBoundary &&
            compareDecimalStrings(acceptedId, currentAfterResponseBoundary) < 0
          ) {
            appliedMarkSequenceByRoRef.current.set(
              cleanRoId,
              markRequestSequence
            );
            return data;
          }

          const replay = getReplayTransitions(generation, startRevision);
          if (!replay.complete) {
            scheduleRefreshUnread();
            return data;
          }

          appliedMarkSequenceByRoRef.current.set(
            cleanRoId,
            markRequestSequence
          );
          const transition = recordTransition(
            {
              type: "markReadResult",
              roId: cleanRoId,
              lastReadMessageId: acceptedId,
              unread: data?.unread === true,
              updatedAt: safeString(data?.updatedAt).trim(),
              markRequestSequence,
            },
            { dispatchNow: false }
          );

          dispatch({
            type: "APPLY_TRANSITION_WITH_REPLAY",
            transition,
            replayTransitions: replay.transitions,
          });

          if (data?.unread === true) {
            const pending = unresolvedUnreadRefreshByRoRef.current;
            if (!pending.has(cleanRoId)) {
              pending.add(cleanRoId);
              Promise.resolve()
                .then(() => refreshUnread())
                .catch((error) => {
                  console.error(error);
                })
                .finally(() => {
                  pending.delete(cleanRoId);
                });
            }
          }
        }

        return data;
      } catch (error) {
        handleCurrentSessionAuthError(
          error,
          requestStoreId,
          requestUserId,
          generation
        );
        throw error;
      } finally {
        const currentInFlight = inFlightMarksRef.current.get(cleanRoId);
        if (
          currentInFlight?.generation === generation &&
          currentInFlight?.lastReadMessageId === cleanMessageId &&
          currentInFlight?.markRequestSequence === markRequestSequence
        ) {
          inFlightMarksRef.current.delete(cleanRoId);
        }
      }
    },
    [
      handleCurrentSessionAuthError,
      getReplayTransitions,
      isCurrentSession,
      isImpersonating,
      isReady,
      recordTransition,
      refreshUnread,
      requestJson,
      scheduleRefreshUnread,
      storeId,
      userId,
    ]
  );

  useEffect(() => {
    if (!isReady || typeof subscribeToRoMessageCreated !== "function") {
      return undefined;
    }

    return subscribeToRoMessageCreated((payload) => {
      if (!payload || typeof payload !== "object") return;

      const payloadStoreId = safeString(payload.storeId).trim();
      const payloadRoId = safeString(payload.roId).trim();
      const message = normalizeServerMessage(payload.message);

      if (!payloadStoreId || !payloadRoId || !message.id) return;
      if (payloadStoreId !== storeIdRef.current) return;
      if (message.storeId !== payloadStoreId || message.roId !== payloadRoId) {
        return;
      }
      if (message.authorUserId === userIdRef.current) return;

      recordTransition({
        type: "messageCreated",
        storeId: payloadStoreId,
        roId: payloadRoId,
        messageId: message.id,
        authorUserId: message.authorUserId,
        createdAt: message.createdAt,
      });
    });
  }, [isReady, recordTransition, subscribeToRoMessageCreated]);

  useEffect(() => {
    if (!isReady || typeof subscribeToRoMessagesRead !== "function") {
      return undefined;
    }

    return subscribeToRoMessagesRead((payload) => {
      const event = normalizeReadEvent(payload);

      if (
        event.storeId !== storeIdRef.current ||
        event.userId !== userIdRef.current ||
        !event.roId ||
        !event.lastReadMessageId
      ) {
        return;
      }

      recordTransition({
        type: "messagesRead",
        storeId: event.storeId,
        roId: event.roId,
        userId: event.userId,
        lastReadMessageId: event.lastReadMessageId,
        updatedAt: event.updatedAt,
      });
    });
  }, [isReady, recordTransition, subscribeToRoMessagesRead]);

  useEffect(() => {
    if (!isReady || typeof subscribeToRoMessageRetracted !== "function") {
      return undefined;
    }

    return subscribeToRoMessageRetracted((payload) => {
      const payloadStoreId = safeString(payload?.storeId).trim();
      const payloadRoId = safeString(payload?.roId).trim();
      const message = normalizeServerMessage(payload?.message);
      if (payloadStoreId !== storeIdRef.current) return;
      if (
        !payloadRoId ||
        !message.id ||
        message.storeId !== payloadStoreId ||
        message.roId !== payloadRoId
      ) {
        return;
      }

      recordTransition({
        type: "messageRetracted",
        storeId: payloadStoreId,
        roId: payloadRoId,
        messageId: message.id,
      });
      scheduleRefreshUnread();
    });
  }, [
    isReady,
    recordTransition,
    scheduleRefreshUnread,
    subscribeToRoMessageRetracted,
  ]);

  const hasUnread = useCallback(
    (roId) => {
      const cleanRoId = safeString(roId).trim();
      return !!(cleanRoId && state.byRo[cleanRoId]?.unread);
    },
    [state.byRo]
  );

  const getUnreadState = useCallback(
    (roId) => {
      const cleanRoId = safeString(roId).trim();
      if (!cleanRoId) return EMPTY_UNREAD;
      return state.byRo[cleanRoId] || { ...EMPTY_UNREAD, roId: cleanRoId };
    },
    [state.byRo]
  );

  const value = useMemo(
    () => ({
      hasUnread,
      getUnreadState,
      refreshUnread,
      markRead,
      isHydrated: state.isHydrated,
      isLoading: state.isLoading,
      error: state.error,
      storeId,
      canMutateReadState: isReady && !isImpersonating,
      isReadOnly: isImpersonating,
      compareDecimalStrings,
      isDecimalMessageId,
    }),
    [
      getUnreadState,
      hasUnread,
      isImpersonating,
      isReady,
      markRead,
      refreshUnread,
      state.error,
      state.isHydrated,
      state.isLoading,
      storeId,
    ]
  );

  return (
    <ROUnreadContext.Provider value={value}>
      {children}
    </ROUnreadContext.Provider>
  );
}

export function useROUnread() {
  return (
    useContext(ROUnreadContext) || {
      hasUnread: () => false,
      getUnreadState: () => EMPTY_UNREAD,
      refreshUnread: () => Promise.resolve(null),
      markRead: () => Promise.resolve(null),
      isHydrated: false,
      isLoading: false,
      error: "",
      storeId: "",
      canMutateReadState: false,
      isReadOnly: false,
      compareDecimalStrings,
      isDecimalMessageId,
    }
  );
}
